import { createFileRoute } from '@tanstack/react-router';

import { pageHead } from '../lib/seo';
import { canonical } from '../lib/site';
import { LEGAL, orBlank } from '../lib/legal';
import { PageContainer } from '../components/layout/page-container';
import { PageHeader } from '../components/ui/page-header';
import { Article, LegalDoc, List } from '../features/legal/legal-doc';

const PRIVACY_DESCRIPTION = '좋소 과수원 개인정보처리방침.';

export const Route = createFileRoute('/privacy')({
  head: () => pageHead({ title: '개인정보처리방침', description: PRIVACY_DESCRIPTION, url: canonical('/privacy'), type: 'website' }),
  component: PrivacyPage,
});

/**
 * 개인정보처리방침. **실제로 수집하는 것만 적는다.**
 *
 * 로그인은 카카오 하나다. 결제는 없다. 조회수 중복 제거용 쿠키(`zoxo_v`)가
 * 있다. 위탁처는 코드가 실제로 부르는 곳(Supabase·Cloudflare·카카오·OpenAI·
 * Google Fonts)이다. 이 목록이 바뀌면 코드와 같이 이 문서도 바꾼다.
 */
function PrivacyPage() {
  const S = LEGAL.service;

  return (
    <PageContainer>
      <PageHeader title="개인정보처리방침" description={`시행일 ${LEGAL.effectiveDate}`} />
      <LegalDoc>
        <Article title="제1조 (개인정보처리방침의 의의)">
          <p>
            {orBlank(LEGAL.operator)}(이하 「운영자」)는 {LEGAL.domain} 에서 제공하는 {S} 서비스(이하
            「서비스」)를 운영하면서 「개인정보 보호법」 등 관련 법령을 지키고, 이용자의 개인정보를 어떻게
            수집·이용·보관·파기하는지 이 방침으로 알립니다.
          </p>
        </Article>

        <Article title="제2조 (서비스 이용과 개인정보 수집)">
          <p>
            공고 열람 등 서비스의 대부분은 가입 없이 이용할 수 있습니다. 댓글·투표·프로필처럼 이용자를 구별해야
            하는 기능을 쓸 때만 로그인을 요구하며, 그때 필요한 최소한의 정보만 수집합니다.
          </p>
        </Article>

        <Article title="제3조 (수집하는 개인정보 항목)">
          <List
            items={[
              <>
                <b>카카오 로그인 시</b>: 카카오가 제공하는 계정 식별자, 이메일, 프로필 닉네임·프로필 사진.
                로그인은 Supabase Auth 를 통해 처리됩니다.
              </>,
              <>
                <b>가입 후 이용자가 직접 입력하는 정보</b>: 닉네임, 회사명(또는 이니셜), 프로필 사진, 직무·직책·
                소개·강점 등 프로필 항목. 입력은 선택이며 입력한 범위에서만 저장됩니다.
              </>,
              <>
                <b>서비스 이용 과정에서 생기는 정보</b>: 댓글·글·설문 응답·반응 등 이용자가 남긴 내용, 접속
                기록(IP 주소, 브라우저 종류, 접속 일시), 조회수 중복 집계를 막기 위한 익명 식별 쿠키(
                <code>zoxo_v</code>).
              </>,
              <>
                <b>AI 진단 기능 이용 시</b>: 이용자가 진단을 위해 입력한 내용과 그 결과.
              </>,
            ]}
          />
        </Article>

        <Article title="제4조 (개인정보의 이용 목적)">
          <List
            items={[
              '회원 식별, 로그인 유지, 계정 관리',
              '댓글·투표·프로필 등 참여 기능의 제공과 작성자 표시',
              '조회수 집계, 서비스 개선을 위한 통계 분석',
              '부정 이용 방지와 이용 제한, 분쟁 대응',
              'AI 진단 등 이용자가 요청한 기능의 제공',
              '법령상 의무의 이행',
            ]}
          />
        </Article>

        <Article title="제5조 (개인정보의 제3자 제공과 처리 위탁)">
          <p>
            운영자는 이용자의 사전 동의 없이 개인정보를 외부에 제공하지 않습니다. 다만 법령에 따라 요구되는
            경우는 예외입니다.
          </p>
          <p>서비스 운영을 위해 다음 업체에 처리를 위탁합니다.</p>
          <List
            items={[
              <>
                <b>Supabase Inc.</b> — 회원 인증, 데이터베이스와 파일 저장(서버 위치: AWS 서울 리전)
              </>,
              <>
                <b>Cloudflare, Inc.</b> — 콘텐츠 전송, 트래픽 보안(접속 시 IP 주소가 전달됩니다)
              </>,
              <>
                <b>카카오(주식회사 카카오)</b> — 카카오 로그인, 카카오톡 공유
              </>,
              <>
                <b>OpenAI, L.L.C.</b> — AI 진단 기능에서 이용자가 입력한 내용의 처리(진단 기능을 쓸 때만)
              </>,
              <>
                <b>Google LLC</b> — 웹 글꼴 제공(Google Fonts; 글꼴을 내려받을 때 IP 주소가 전달됩니다)
              </>,
            ]}
          />
          <p>
            위 업체 중 일부는 국외에 있습니다. 위탁 업체는 계약에 따라 위탁 목적 밖으로 개인정보를 이용할 수
            없습니다. 지원사업 공고문의 자동 해석은 각 기관이 공개한 공고 문서만을 대상으로 하며, 이용자의
            개인정보는 여기에 쓰이지 않습니다.
          </p>
        </Article>

        <Article title="제6조 (개인정보의 보관 기간과 파기)">
          <List
            items={[
              '이용자가 탈퇴하면 계정 정보(식별자, 이메일, 닉네임, 프로필)는 지체 없이 삭제합니다.',
              '댓글 등 게시물은 다른 이용자와의 대화 맥락을 지키기 위해 작성자 표시를 지운 채 남을 수 있습니다. 삭제를 원하면 탈퇴 전에 직접 삭제하거나 운영자에게 요청할 수 있습니다.',
              '부정 이용의 재발을 막기 위한 기록은 1년 동안 보관합니다.',
              '「통신비밀보호법」에 따른 접속 기록은 3개월 동안 보관합니다.',
              '그 밖에 법령이 보관을 요구하는 정보는 그 법령이 정한 기간 동안 보관합니다.',
              '보관 기간이 지난 정보는 복구할 수 없는 방법으로 삭제합니다.',
            ]}
          />
        </Article>

        <Article title="제7조 (쿠키와 유사 기술)">
          <List
            items={[
              <>
                <code>zoxo_v</code>: 같은 사람의 조회를 두 번 세지 않기 위한 익명 식별자입니다. 개인을 알아볼 수
                있는 정보를 담지 않습니다.
              </>,
              '로그인 세션: 로그인 상태를 유지하기 위해 브라우저에 저장됩니다.',
              '이용자는 브라우저 설정에서 쿠키 저장을 거부할 수 있습니다. 거부하면 로그인 유지 등 일부 기능이 동작하지 않을 수 있습니다.',
              '운영자는 별도의 광고·행태정보 추적 도구를 쓰지 않습니다.',
            ]}
          />
        </Article>

        <Article title="제8조 (이용자의 권리)">
          <List
            items={[
              '이용자는 언제든지 자신의 개인정보를 열람·정정·삭제하거나 처리 정지를 요구할 수 있습니다. 프로필 정보는 서비스 안에서 직접 고칠 수 있고, 그 밖의 요청은 아래 연락처로 하면 됩니다.',
              '정정을 요청한 정보는 처리가 끝날 때까지 이용하거나 제공하지 않습니다.',
              '탈퇴는 서비스 안에서 직접 할 수 있으며 즉시 처리됩니다.',
              '이용자는 자신의 계정과 비밀번호(카카오 계정)를 안전하게 관리할 책임이 있습니다.',
            ]}
          />
        </Article>

        <Article title="제9조 (개인정보의 안전성 확보 조치)">
          <List
            items={[
              '서비스와 서버 사이의 모든 통신을 암호화(HTTPS)합니다.',
              '데이터베이스는 행 단위 접근 제어(Row Level Security)를 적용하고, 개인정보에 접근할 수 있는 사람을 최소화합니다.',
              '비밀번호는 저장하지 않습니다. 인증은 카카오와 Supabase Auth 가 담당합니다.',
              '접근 기록을 보관하고, 외부 위탁 업체의 보안 수준을 확인합니다.',
            ]}
          />
        </Article>

        <Article title="제10조 (개인정보 보호책임자와 문의)">
          <List
            items={[
              <>개인정보 보호책임자: {orBlank(LEGAL.officer)}</>,
              <>이메일: {orBlank(LEGAL.email)}</>,
            ]}
          />
          <p>개인정보 침해에 대한 신고나 상담이 필요하면 다음 기관에 문의할 수 있습니다.</p>
          <List
            items={[
              '개인정보침해신고센터 (privacy.kisa.or.kr, 국번 없이 118)',
              '개인정보분쟁조정위원회 (www.kopico.go.kr, 1833-6972)',
              '대검찰청 사이버수사과 (www.spo.go.kr, 국번 없이 1301)',
              '경찰청 사이버수사국 (ecrm.police.go.kr, 국번 없이 182)',
            ]}
          />
        </Article>

        <Article title="제11조 (방침의 변경)">
          <p>
            이 방침의 내용이 바뀌면 시행일 7일 전부터 서비스 안에 공지합니다. 수집 항목이나 이용 목적이
            늘어나는 등 이용자에게 중요한 변경은 30일 전부터 공지합니다.
          </p>
        </Article>

        <Article title="부칙">
          <p>이 방침은 {LEGAL.effectiveDate}부터 시행합니다.</p>
        </Article>
      </LegalDoc>
    </PageContainer>
  );
}
